import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Audio + video are served from media.<env>.saysomethingin.com, not shipped
// in the repo. `VITE_MEDIA_BASE` is set per-environment by the deploy
// workflows; when unset (e.g. local dev with no .env), media URLs become
// relative and 404 gracefully — posters/portraits still render.
const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE || '';

export function TestimonialFeature({ t }) {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const play = () => {
    setPlaying(true);
    setTimeout(() => {
      if (videoRef.current) videoRef.current.play().catch(()=>{});
    }, 20);
  };

  return (
    <section className="ssi-testi" style={testiStyles.wrap}>
      <div className="ssi-testi__inner" style={testiStyles.inner}>
        <div className="ssi-testi__media" style={testiStyles.media}>
          <div className="ssi-testi__phoneFrame" style={testiStyles.phoneFrame}>
            {!playing && (
              <button onClick={play} style={testiStyles.playBtn} aria-label="Play testimonial">
                <img src="/assets/testimonial-ruth-bbc-poster.jpg" alt="" style={testiStyles.poster}/>
                <span style={testiStyles.playCircle}>
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                    <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
                  </svg>
                </span>
                <span style={testiStyles.duration}>0:49</span>
              </button>
            )}
            {playing && (
              <video ref={videoRef} src={`${MEDIA_BASE}/testimonials/featured/ruth-jones-bbc.mp4`}
                controls playsInline style={testiStyles.video}
                poster="/assets/testimonial-ruth-bbc-poster.jpg"/>
            )}
          </div>
          <div style={testiStyles.credit}>{t.testiCredit}</div>
        </div>
        <div className="ssi-testi__copy" style={testiStyles.copy}>
          <div style={testiStyles.kicker}>{t.testiKicker}</div>
          <blockquote style={testiStyles.quote}>
            <span style={testiStyles.openQuote}>“</span>{t.testiQuote}<span style={testiStyles.closeQuote}>”</span>
          </blockquote>
          <div style={testiStyles.attribution}>
            <div style={testiStyles.name}>{t.testiName}</div>
            <div style={testiStyles.role}>{t.testiRole}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const CELEBS = [
  { slug:'ruth-jones',            name:'Ruth Jones',            role:'Actor & writer',         src:`${MEDIA_BASE}/testimonials/celebrities/ruth-jones.mp4` },
  { slug:'scott-quinnell',        name:'Scott Quinnell',        role:'Rugby legend',           src:`${MEDIA_BASE}/testimonials/celebrities/scott-quinnell.mp4` },
  { slug:'katie-owen',            name:'Katie Owen',            role:'Actor',                  src:`${MEDIA_BASE}/testimonials/celebrities/katie-owen.mp4` },
  { slug:'kiri-pritchard-mclean', name:'Kiri Pritchard-McLean', role:'Comedian',               src:`${MEDIA_BASE}/testimonials/celebrities/kiri-pritchard-mclean.mp4` },
  // chris-coleman is in `celebrities/` rather than `testimonials/celebrities/`
  // (same single MP4 used for both his Iaith ar Daith page and this strip).
  // Flag for Ivan if he'd rather we duplicate it under testimonials/celebrities/.
  { slug:'chris-coleman',         name:'Chris Coleman',         role:'Football manager',       src:`${MEDIA_BASE}/celebrities/chris-coleman.mp4` },
  { slug:'amanda-henderson',      name:'Amanda Henderson',      role:'Actor',                  src:`${MEDIA_BASE}/testimonials/celebrities/amanda-henderson.mp4` },
  { slug:'rakie-ayola',           name:'Rakie Ayola',           role:'Actor',                  src:`${MEDIA_BASE}/testimonials/celebrities/rakie-ayola.mp4` },
  { slug:'steve-backshall',       name:'Steve Backshall',       role:'Explorer & presenter',   src:`${MEDIA_BASE}/testimonials/celebrities/steve-backshall.mp4` },
  { slug:'carol-vorderman',       name:'Carol Vorderman',       role:'TV host & educator',     src:`${MEDIA_BASE}/testimonials/celebrities/carol-vorderman.mp4` },
];

export function CelebritiesStrip({ t }) {
  const [openCeleb, setOpenCeleb] = useState(null);

  useEffect(() => {
    if (!openCeleb) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenCeleb(null); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openCeleb]);

  return (
    <section className="ssi-celeb" style={celebStyles.wrap}>
      <div style={celebStyles.head}>
        <div style={celebStyles.kicker}>{t.celebKicker}</div>
        <h2 style={celebStyles.h2}>{t.celebTitle}</h2>
        <p style={celebStyles.sub}>{t.celebSub}</p>
        {t.celebLeadIn ? <p style={celebStyles.leadIn}>{t.celebLeadIn}</p> : null}
      </div>
      <div className="ssi-celeb__row" style={celebStyles.row}>
        {CELEBS.map(c => (
          <button key={c.slug} onClick={()=>setOpenCeleb(c)} style={celebStyles.tile} title={`${c.name} – watch testimonial`}>
            <div style={celebStyles.tileImgWrap}>
              <img src={`/assets/celeb-${c.slug}.jpg`} alt={c.name} style={celebStyles.tileImg}/>
              <span style={celebStyles.tilePlay}>
                <svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                  <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
                </svg>
              </span>
            </div>
            <div style={celebStyles.tileName}>{c.name}</div>
            <div style={celebStyles.tileRole}>{c.role}</div>
          </button>
        ))}
      </div>
      <div style={celebStyles.foot}>
        <Link to="/celebrity-coaching" style={celebStyles.seeAll}>{t.celebMoreLink}</Link>
      </div>
      {openCeleb && <VideoModal celeb={openCeleb} onClose={()=>setOpenCeleb(null)}/>}
    </section>
  );
}

export const LEARNERS = [
  { slug:'leigh-ann', name:'Leigh-Ann', role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/leigh-ann.mov` },
  { slug:'bethan',    name:'Bethan',    role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/bethan.mp4` },
  { slug:'erica',     name:'Erica',     role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/erica.mp4` },
  { slug:'rob',       name:'Rob',       role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/rob.mp4` },
  { slug:'ailein',    name:'Ailéin',    role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/ailein.mp4` },
  { slug:'geraint',   name:'Geraint',   role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/geraint.mp4` },
  { slug:'jamie',     name:'Jamie',     role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/jamie.mp4` },
  { slug:'neil',      name:'Neil',      role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/neil.mp4` },
  { slug:'nina',      name:'Nina',      role:'Welsh learner',  src:`${MEDIA_BASE}/testimonials/learners/nina.mp4` },
  { slug:'rhian',     name:'Rhian',     role:'Spanish learner', src:`${MEDIA_BASE}/testimonials/learners/rhian.mov` },
  { slug:'irina',     name:'Irina',     role:'Spanish learner', src:`${MEDIA_BASE}/testimonials/learners/irina.mp4` },
];

export function LearnersStrip({ t }) {
  const [openLearner, setOpenLearner] = useState(null);

  useEffect(() => {
    if (!openLearner) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpenLearner(null); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openLearner]);

  return (
    <section className="ssi-learner" style={learnerStyles.wrap}>
      <div style={learnerStyles.inner}>
        <div style={learnerStyles.head}>
          <div style={learnerStyles.kicker}>{t.learnerKicker}</div>
          <h2 style={learnerStyles.h2}>{t.learnerTitle}</h2>
          <p style={learnerStyles.sub}>{t.learnerSub}</p>
        </div>
        <div className="ssi-learner__row" style={learnerStyles.row}>
          {LEARNERS.map(l => (
            <button key={l.slug} onClick={()=>setOpenLearner(l)} style={learnerStyles.tile} title={`${l.name} – watch testimonial`}>
              <div style={learnerStyles.tileImgWrap}>
                <img src={`/assets/learner-${l.slug}.jpg`} alt={l.name} style={learnerStyles.tileImg}/>
                <span style={learnerStyles.tilePlay}>
                  <svg width="14" height="14" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                    <path d="M10 7v14l11-7-11-7z" fill="#fff"/>
                  </svg>
                </span>
              </div>
              <div style={learnerStyles.tileName}>{l.name}</div>
              <div style={learnerStyles.tileRole}>{l.role}</div>
            </button>
          ))}
        </div>
      </div>
      {openLearner && <VideoModal celeb={openLearner} onClose={()=>setOpenLearner(null)}/>}
    </section>
  );
}

export function VideoModal({ celeb, onClose }) {
  return (
    <div style={modalStyles.backdrop} onClick={onClose}>
      <div style={modalStyles.dialog} onClick={e=>e.stopPropagation()}>
        <button onClick={onClose} style={modalStyles.close} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <video
          src={celeb.src}
          poster={`/assets/celeb-${celeb.slug}.jpg`}
          controls autoPlay playsInline
          style={modalStyles.video}
        />
        <div style={modalStyles.caption}>
          <div style={modalStyles.capName}>{celeb.name}</div>
          <div style={modalStyles.capRole}>{celeb.role}</div>
        </div>
      </div>
    </div>
  );
}

const testiStyles = {
  wrap: { background:'var(--brandRed)', color:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1080, margin:'0 auto', display:'grid', gridTemplateColumns:'auto 1fr', alignItems:'center', gap:64, textAlign:'left' },
  media: { display:'flex', flexDirection:'column', alignItems:'center', gap:14 },
  phoneFrame: { width:320, borderRadius:36, overflow:'hidden', border:'10px solid #1a0200', background:'#000', boxShadow:'0 40px 70px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.4)' },
  playBtn: { position:'relative', display:'block', width:'100%', padding:0, border:'none', background:'none', cursor:'pointer' },
  poster: { width:'100%', height:'auto', display:'block', aspectRatio:'688/1264', objectFit:'cover' },
  playCircle: { position:'absolute', top:'50%', left:'50%', width:72, height:72, marginLeft:-36, marginTop:-36, borderRadius:'50%', background:'rgba(0,0,0,.55)', border:'2px solid rgba(255,255,255,.9)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' },
  duration: { position:'absolute', right:12, bottom:12, background:'rgba(0,0,0,.6)', color:'#fff', fontSize:11, padding:'3px 7px', borderRadius:5, fontVariantNumeric:'tabular-nums' },
  video: { width:'100%', aspectRatio:'688/1264', display:'block', background:'#000' },
  credit: { fontSize:12, letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(255,255,255,.7)', fontWeight:500 },
  copy: { maxWidth:620, textAlign:'left', display:'flex', flexDirection:'column', alignItems:'flex-start' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--gold)', fontWeight:600, marginBottom:18 },
  quote: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.2, margin:'0 0 28px', color:'#fff', letterSpacing:'-.005em', textWrap:'pretty' },
  openQuote: { color:'var(--gold)', marginRight:4 },
  closeQuote: { color:'var(--gold)', marginLeft:4 },
  attribution: { marginBottom:22 },
  name: { fontFamily:'var(--font-display)', fontSize:20, color:'#fff' },
  role: { fontSize:14, color:'rgba(255,255,255,.75)', marginTop:2 },
  more: { display:'inline-block', color:'#fff', borderBottom:'1px solid rgba(255,255,255,.55)', paddingBottom:2, fontSize:14, fontWeight:500, textDecoration:'none' },
};

const celebStyles = {
  wrap: { padding:'80px 32px 48px', maxWidth:1440, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:680, margin:'0 auto 40px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:34, lineHeight:1.15, margin:'0 0 10px', letterSpacing:'-.01em' },
  sub: { fontSize:15, color:'var(--grey)', margin:0 },
  leadIn: { fontFamily:'var(--font-display)', fontSize:22, lineHeight:1.4, color:'var(--fg-1)', margin:'20px auto 0', maxWidth:620, textWrap:'balance', fontStyle:'italic' },
  row: { display:'grid', gridTemplateColumns:'repeat(9,minmax(0,1fr))', gap:14, alignItems:'start' },
  tile: { textDecoration:'none', color:'inherit', display:'flex', flexDirection:'column', textAlign:'left', background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', minWidth:0, overflow:'hidden' },
  tileImgWrap: { position:'relative', aspectRatio:'1/1', borderRadius:14, overflow:'hidden', background:'var(--offWhite)', marginBottom:10 },
  tileImg: { width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 28%', display:'block', transition:'transform 300ms' },
  tilePlay: { position:'absolute', right:8, bottom:8, width:26, height:26, borderRadius:'50%', background:'rgba(0,0,0,.62)', display:'flex', alignItems:'center', justifyContent:'center', paddingLeft:2 },
  tileName: { fontSize:13, fontWeight:500, color:'var(--fg-1)', lineHeight:1.2, overflowWrap:'break-word', hyphens:'auto' },
  tileRole: { fontSize:11, color:'var(--grey)', marginTop:2, lineHeight:1.3, overflowWrap:'break-word' },
  foot: { textAlign:'center', marginTop:36 },
  seeAll: { fontSize:14, color:'var(--brandRed)', fontWeight:500, textDecoration:'none', borderBottom:'1px solid var(--brandRed)', paddingBottom:2 },
};

const learnerStyles = {
  wrap: { background:'var(--offWhite, #faf7f2)', padding:'64px 32px 56px' },
  inner: { maxWidth:1440, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:680, margin:'0 auto 40px' },
  kicker: { fontSize:12, letterSpacing:'.14em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:600, marginBottom:10 },
  h2: { fontFamily:'var(--font-display)', fontSize:30, lineHeight:1.15, margin:'0 0 10px', letterSpacing:'-.01em' },
  sub: { fontSize:15, color:'var(--grey)', margin:0 },
  row: { display:'grid', gridTemplateColumns:'repeat(11,minmax(0,1fr))', gap:12 },
  tile: { textDecoration:'none', color:'inherit', display:'block', textAlign:'left', background:'none', border:'none', padding:0, cursor:'pointer', font:'inherit', minWidth:0, overflow:'hidden' },
  tileImgWrap: { position:'relative', aspectRatio:'1/1', borderRadius:12, overflow:'hidden', background:'#fff', marginBottom:8, boxShadow:'0 1px 2px rgba(0,0,0,.04)' },
  tileImg: { width:'100%', height:'100%', objectFit:'cover', objectPosition:'center 28%', display:'block' },
  tilePlay: { position:'absolute', right:6, bottom:6, width:22, height:22, borderRadius:'50%', background:'rgba(0,0,0,.62)', display:'flex', alignItems:'center', justifyContent:'center', paddingLeft:2 },
  tileName: { fontSize:12, fontWeight:500, color:'var(--fg-1)', lineHeight:1.2, overflowWrap:'break-word' },
  tileRole: { fontSize:10.5, color:'var(--grey)', marginTop:2, lineHeight:1.3, overflowWrap:'break-word' },
  foot: { textAlign:'center', marginTop:32 },
  seeAll: { fontSize:14, color:'var(--brandRed)', fontWeight:500, textDecoration:'none', borderBottom:'1px solid var(--brandRed)', paddingBottom:2 },
};

const modalStyles = {
  backdrop: { position:'fixed', inset:0, background:'rgba(20,0,0,.82)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'32px' },
  dialog: { position:'relative', width:'min(1040px, 100%)', maxHeight:'90vh', display:'flex', flexDirection:'column' },
  close: { position:'absolute', top:-44, right:0, width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.3)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', padding:0 },
  video: { width:'100%', maxHeight:'80vh', borderRadius:14, background:'#000', display:'block' },
  caption: { marginTop:16, color:'#fff', textAlign:'center' },
  capName: { fontFamily:'var(--font-display)', fontSize:20 },
  capRole: { fontSize:13, color:'rgba(255,255,255,.7)', marginTop:2 },
};
